import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { acquireStartupOverlay, lockOverlayScroll } from './startupOverlayState';
import { ConnectSplashScene, CONNECT_LOGO_FADE_SECONDS } from './ConnectSplashScene';
import './startupSplash.css';

const CONNECTED_SCENE_HOLD_MS = 1000;
const LOGO_START_MS = 3700 + CONNECTED_SCENE_HOLD_MS;
const REVEAL_START_MS = LOGO_START_MS + CONNECT_LOGO_FADE_SECONDS * 1000 + 600;
export const SPLASH_TIMING = { release: 1200, impact: 2000, connecting: 2500, logo: LOGO_START_MS, reveal: REVEAL_START_MS, complete: REVEAL_START_MS + 600 };
export const REDUCED_SPLASH_TIMING = { reveal: 800, complete: 1000 };

export const StartupSplash = ({ onComplete }) => {
  const reducedMotion = useReducedMotion();
  const [phase, setPhase] = useState('entering');
  const [done, setDone] = useState(false);
  const complete = useRef(onComplete);
  complete.current = onComplete;
  const finished = useRef(false);
  const surface = useRef(null);
  const skip = useRef(null);
  const finish = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    setDone(true);
    complete.current?.();
  }, []);

  useLayoutEffect(() => {
    if (done) return;
    const previousFocus = document.activeElement;
    const unblock = acquireStartupOverlay();
    const unlock = lockOverlayScroll();
    surface.current?.focus({ preventScroll: true });
    return () => {
      // Unlock before the paused carousel re-acquires its own lock.
      unlock();
      unblock();
      if (previousFocus?.isConnected && !previousFocus.closest?.('[inert]')) previousFocus.focus?.({ preventScroll: true });
    };
  }, [done]);

  useEffect(() => {
    if (done) return;
    const timing = reducedMotion ? REDUCED_SPLASH_TIMING : SPLASH_TIMING;
    const timers = [];
    setPhase(reducedMotion ? 'logo' : 'entering');
    if (!reducedMotion) {
      ['release', 'impact', 'connecting', 'logo'].forEach(next => {
        timers.push(setTimeout(() => setPhase(next), timing[next]));
      });
    }
    timers.push(setTimeout(() => setPhase('revealing'), timing.reveal));
    timers.push(setTimeout(finish, timing.complete));
    return () => timers.forEach(clearTimeout);
  }, [reducedMotion, finish, done]);

  if (done) return null;

  const keyboard = event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); finish(); }
    if (event.key === 'Tab') { event.preventDefault(); skip.current?.focus(); }
  };
  return createPortal(
    <motion.div ref={surface} className="startup-splash" tabIndex={-1} role="status" aria-label="Opening Par Seeker Connect"
      aria-live="polite" aria-busy="true" data-testid="logo-splash-screen" data-phase={phase}
      data-reduced-motion={!!reducedMotion} onKeyDown={keyboard}
      initial={{ opacity: 1 }} animate={{ opacity: phase === 'revealing' ? 0 : 1 }}
      transition={{ duration: reducedMotion ? .2 : (SPLASH_TIMING.complete - SPLASH_TIMING.reveal) / 1000, ease: 'easeInOut' }}>
      <ConnectSplashScene phase={phase} reducedMotion={!!reducedMotion} onAssetError={finish} />
      <button ref={skip} type="button" data-testid="splash-skip-button" className="startup-splash-skip" onClick={finish}>Skip intro</button>
    </motion.div>, document.body
  );
};